import { AddLineTool } from './AddLineTool.js'
import { Line } from './Line.js'
import { Point } from './Point.js'
import { AddPointTool } from './AddPointTool.js'
import { GEObject } from './GEObject.js'
import { GreenRenderer } from './GreenRenderer.js'


export class GreenEditor extends GEObject {
	objects = [];
	editMode = true;
	addLineTool = null;
	selectedObject = null;
	deactivateAfterUse = true;
	enabled = true;
	deferUpdates = false;
	renderer = null;
	pixelRatio = 1
	constructor(el, options = {
		pixelsPerMeter: 1,
		pixelRatio: 1
	}) {
		super()
		this.options = options
		this.canvas = el
		this.width = el.width;
		this.height = el.height;
		let context = el.getContext('2d', { alpha: true });
		this.context = context;
		this.renderer = new GreenRenderer(el, context)

		context.imageSmoothingQuality = 'high'
		context.imageSmoothingEnabled = true
		this.setupEditMode()
		this.addPointTool = new AddPointTool(this)
		this.addLineTool = new AddLineTool(this)

		this.addLineTool.on('isActive', (source, isActive, props) => {
			this.trigger('currentTool', isActive ? 'line' : null, props)
		})
		this.addPointTool.on('isActive', (source, isActive, props) => {
			this.trigger('currentTool', isActive ? 'point' : null, props)
		})
		this.addLineTool.on('add', (source, line) => {
			this.addObject(line)
			if(this.deactivateAfterUse) {
				this.addLineTool.deactivate()
				this.editMode = true
				line.setSelected(true)
			} else {
				line.setSelected(false)
			}
		})
		this.addLineTool.on('render', (source, line) => {
			if(line) {
				this.render(line)
			} else {
				this.render()
			}
		})

		this.addPointTool.on('add', (source, point) => {
			this.addObject(point)
			if(this.deactivateAfterUse) {
				this.addPointTool.deactivate()
				this.editMode = true
			}
		})
		this.addPointTool.on('render', (source, point) => {
			this.render(point)
		})
		
		document.addEventListener('keydown', (e) => {
			if(!this.enabled) {
				return
			}
			if(e.key === 'Escape') {
				if(this.addLineTool.isActive) {
					this.addLineTool.deactivate()
				}
				if(this.addPointTool.isActive) {
					this.addPointTool.deactivate()
				}
				if(this.selectedObject) {
					this.deselect(this.selectedObject)
				}
				this.editMode = true
				this.canvas.style.cursor = 'default'
			} else if(e.key === 'Delete' || e.key === 'Backspace') {
				if(this.selectedObject) {
					this.deleteObject(this.selectedObject)
				}
			}
		});
	}
	enable() {
		this.enabled = true
		this.canvas.style.opacity = 1
	}
	disable() {
		this.enabled = false
		this.canvas.style.opacity = 0.5
	}
	load(data) {
		this.deferUpdates = true
		let array = GreenEditor.readObjects(data)
		for(let o of array) {
			this.addObject(o)
		}
		this.deferUpdates = false
		this.render()
	}
	getData() {
		return JSON.parse(JSON.stringify(this.objects))
	}
	getHandleForPoint(point) {
		let handles = [] 
		for(let l of this.objects) {
			if(l.locked) {
				continue
			}
			let handle = l.handleForPoint(point)
			if(handle) {
				handles.push(handle)
			}
		}
		return handles
	}
	highlightObjectsAtPoint(point) {
		let hasHighlighted = false
		for(let l of this.objects) {
			if(l.locked) {
				continue
			}
			let over = this.selectedObject && 
				(this.getHandleForPoint(point).includes(this.selectedObject) ||
				(this.selectedObject.parent && this.getHandleForPoint(point).includes(this.selectedObject.parent)))
			if(!hasHighlighted && l.isInside(point) && !over) {
				l.setHighlighted(l.isInside(point))
				hasHighlighted = true
			} else {
				l.setHighlighted(false)
			}
		}
		if(hasHighlighted) {
			this.canvas.style.cursor = 'pointer'
		} else {
			this.canvas.style.cursor = 'default'
		}
		this.render()
	}
	updatePixelsPerMeter(ppm) {
		this.options.pixelsPerMeter = ppm
		this.render()
	}
	getPixelRatio() {
		return this.options.pixelRatio ?? 1
	}
	setupEditMode() {
		let canvas = this.canvas
		let isDragging = false	
		let referencePoint

		let mousedown = (event) => {
			if(!this.editMode) {
				return
			}
			if(!this.enabled) {
				return
			}
			let x = event.offsetX * this.getPixelRatio()
			let y = event.offsetY * this.getPixelRatio()
			isDragging = true
			let point = {
				x: x,
				y: y
			}
			referencePoint = point
			let handles = this.getHandleForPoint(point)
			if(handles.length === 0) {
				if(this.selectedObject) {
					this.deselect(this.selectedObject)
				}
				return
			}
			if(this.selectedObject) {
				let handle = handles.find((e) =>  e.id === this.selectedObject.id || (e.parent && e.parent.id == this.selectedObject.id) || (this.selectedObject.parent && e.id === this.selectedObject.parent.id))
				if (handle) {
					this.deselect(this.selectedObject)
					this.select(handle)
				} else {
					this.deselect(this.selectedObject)
					this.select(handles[0])
				}
			} else {
				this.select(handles[0])
			}
		}
		let mouseup = (event) => {
			isDragging = false
		}
		let mousemove = (event) => {
			if(!this.editMode) {
				return
			}
			if(!this.enabled) {
				return
			}
			let x = event.offsetX * this.getPixelRatio()
			let y = event.offsetY * this.getPixelRatio()
			let point = {
				x: x,
				y: y
			}
			if(!this.selectedObject) {
				this.highlightObjectsAtPoint({x: x, y: y})
				return
			}
			if(!isDragging) {
				return
			}
			let dx = point.x - referencePoint.x
			let dy = point.y - referencePoint.y
			referencePoint = point
			this.selectedObject.move(dx, dy)
			this.render()
		}
		canvas.addEventListener('mousedown',mousedown)
		canvas.addEventListener('mouseup', mouseup);
		canvas.addEventListener('mousemove', mousemove);
	}
	deleteObject(object) {
		let _o
		if(object instanceof Line) {
			_o = object
		} else if(object instanceof Point) {
			if(object.parent) {
				_o = object.parent	
			} else {
				_o = object
			}
		}
		if(!_o) {
			return
		}
		_o.removeAllEventListerners()
		let index = this.objects.indexOf(_o)
		if(index === -1) {
			return
		}
		this.objects.splice(index, 1)
		this.render()
		if(this.selectedObject === object) {
			this.deselect(this.selectedObject)
		}
		this.trigger('objectDeleted', object)
	}
	addObject(object) {
		if(!object) {
			return
		}
		if(this.selectedObject) {
			this.selectedObject.unfocus()
		}
		this.objects.push(object)
		let _this = this
		if(this.deferUpdates == false ) {
			this.select(object)
		}
		object.on('changed', function(object, data) {
			_this.trigger('objectChanged', object, data)
			_this.render()
		})
		this.render()
		if(this.deferUpdates == false ) {
			this.trigger('objectAdded', object)
		}
	}
	getObject(id) {
		for(let o of this.objects) {
			if(o.properties && o.properties.id === id) {
				return o
			}
		}
		return null
	}
	deselect(object) {
		if(this.selectedObject === object) {
			this.selectedObject.unfocus()
			this.selectedObject = null
			this.trigger('deselect', object)
		}
	}
	select(object) {
		if(this.selectedObject) {
			this.deselect(this.selectedObject)
		}
		this.selectedObject = object
		object.setSelected(true)
		this.trigger('select', object.parent || object)
	}
	render(object) {
		if(object) {
			this.renderer.render([object, ...this.objects], this.options)
		} else {
			this.renderer.render(this.objects, this.options)
		}
	}
	activateTool(tool, properties, deactivateAfterUse = true) {
		this.tool = tool
		if(this.selectedObject) {
			this.deselect(this.selectedObject)
		}
		this.addLineTool.deactivate()
		this.addPointTool.deactivate()
		this.editMode = true
		this.deactivateAfterUse = deactivateAfterUse
		this.canvas.style.cursor = 'default'
		if(tool === 'line') {
			this.editMode = false
			this.addLineTool.activate(properties)
			this.canvas.style.cursor = 'crosshair'
		} else if (tool === 'point') {
			this.editMode = false
			this.addPointTool.activate(properties)
			this.canvas.style.cursor = 'crosshair'
		} else {
			this.trigger('currentTool', null)
		}
	}
	static readObjects(data, width, height) {
		let objects = []
		let measurementsAdded = false
		for(let d of data) {
			if(d.type === 'Line') {
				let line = new Line(
					new Point(d.p1.x, d.p1.y, d.p1.properties),
					new Point(d.p2.x, d.p2.y, d.p2.properties),
					d.properties
				)
				objects.push(line)
			} else if(d.type === 'Point') {
				let point = new Point(d.x, d.y, d.properties)
				objects.push(point)
			}
		}
		return objects
	}
}